import { Button, CloseButton, Flex, Input, InputBase, Popover, Stack } from '@mantine/core'
import { DatePicker, DatesRangeValue } from '@mantine/dates'
import { IconCalendar } from '@tabler/icons-react'
import React, { useState } from 'react'
import {
	EMPTY_REVENUE_RANGE,
	REVENUE_QUICK_PICKS,
	RevenueDateRange,
	RevenueQuickPick,
	calendarDate,
	calendarDayText,
	matchingQuickPick,
	quickPickRange,
	revenueRangeText,
	vietnamToday,
} from './revenueDateRange'

/**
 * Two months are shown: last month and this month, so today is in view. A range
 * that ends before last month opens on the month it ends in and the one before.
 */
const firstShownMonth = (today: Date, end: Date | null) => {
	const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
	return end && end < lastMonth ? new Date(end.getFullYear(), end.getMonth() - 1, 1) : lastMonth
}

/**
 * The one revenue filter of Manage Learners: a field that opens a two-month
 * calendar with the quick picks on its right. A quick pick applies at once; a
 * hand pick applies when the end day is chosen. Days are Vietnam days.
 */
function RevenueDateRangeField({
	value,
	onChange,
}: {
	value: RevenueDateRange
	onChange: (range: RevenueDateRange) => void
}) {
	const [opened, setOpened] = useState(false)
	// The days clicked on the calendar. A start day alone filters nothing yet.
	const [picked, setPicked] = useState<DatesRangeValue>([null, null])
	const now = new Date()
	const today = calendarDate(vietnamToday(now)) as Date
	const [shownMonth, setShownMonth] = useState(() => firstShownMonth(today, null))
	const text = revenueRangeText(value)
	const activePick = matchingQuickPick(value, now)

	const toggle = () => {
		if (opened) {
			setOpened(false)
			return
		}
		const from = calendarDate(value.from)
		const to = calendarDate(value.to)
		setPicked(from && to ? [from, to] : [null, null])
		setShownMonth(firstShownMonth(today, to))
		setOpened(true)
	}

	const apply = (range: RevenueDateRange) => {
		onChange(range)
		setOpened(false)
	}

	const pickOnCalendar = (next: DatesRangeValue) => {
		setPicked(next)
		const [start, end] = next
		if (start && end) apply({ from: calendarDayText(start), to: calendarDayText(end) })
	}

	const pickQuick = (pick: RevenueQuickPick) => apply(quickPickRange(pick, new Date()))

	return (
		<Popover
			opened={opened}
			onChange={setOpened}
			position="bottom-start"
			shadow="md"
			withinPortal
			trapFocus
			returnFocus
		>
			<Popover.Target shouldOverrideDefaultTargetId={false}>
				<InputBase
					component="button"
					type="button"
					label="Revenue date range"
					icon={<IconCalendar size={16} />}
					onClick={toggle}
					rightSection={
						text ? (
							<CloseButton
								variant="transparent"
								size="sm"
								aria-label="Clear revenue date range"
								onClick={() => apply(EMPTY_REVENUE_RANGE)}
							/>
						) : null
					}
					styles={{
						input: { cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
					}}
					sx={{ width: 250 }}
				>
					{text || <Input.Placeholder>Any</Input.Placeholder>}
				</InputBase>
			</Popover.Target>
			<Popover.Dropdown>
				<Flex gap="md" align="flex-start">
					<DatePicker
						type="range"
						numberOfColumns={2}
						columnsToScroll={1}
						firstDayOfWeek={1}
						allowSingleDateInRange
						maxDate={today}
						value={picked}
						onChange={pickOnCalendar}
						date={shownMonth}
						onDateChange={setShownMonth}
					/>
					<Stack
						spacing={2}
						sx={(theme) => ({
							borderLeft: `1px solid ${theme.colors.gray[3]}`,
							paddingLeft: theme.spacing.md,
							minWidth: 130,
						})}
					>
						{REVENUE_QUICK_PICKS.map((pick) => (
							<Button
								key={pick.value}
								size="xs"
								variant={activePick === pick.value ? 'light' : 'subtle'}
								color={activePick === pick.value ? 'blue' : 'gray'}
								onClick={() => pickQuick(pick.value)}
								styles={{ inner: { justifyContent: 'flex-start' } }}
							>
								{pick.label}
							</Button>
						))}
					</Stack>
				</Flex>
			</Popover.Dropdown>
		</Popover>
	)
}

export default RevenueDateRangeField
